import { tenantApi } from '@shared/api/tenantApi';

/**
 * Surveys & eForms API — `/survey-responses/*` member-facing routes
 * (verified against tenant-api `routes/index.ts`). Responses use the
 * `{ success, data }` envelope; `.data` is unwrapped here.
 */

export interface SurveyView {
  survey_template_id: string;
  flow_template_id: string;
  flow_template_name?: string;
  name: string;
  description?: string | null;
  flow_type: string;
  publication_status: string;
  deadline_at?: string | null;
  link_only: boolean;
}

export interface MyResponse {
  survey_instance_id: string;
  flow_instance_id: string;
  survey_template_id: string;
  submitted_at?: string | null;
  submission_source: string;
  created_at: string;
  name: string;
  description: string | null;
  flow_type: string;
  deadline_at: string | null;
}

export interface SurveyResponse {
  survey_instance_id: string;
  flow_instance_id: string;
  survey_template_id: string;
  submitted_at?: string | null;
  submission_source: string;
  created_at?: string;
}

export interface SubmissionAnswer {
  content_key?: string | null;
  item_key?: string | null;
  name?: string | null;
  question_type?: string | null;
  item_value?: unknown;
  sequence_number?: number | null;
  step_name?: string | null;
  step_sequence?: number | null;
}

export interface SubmittedResponseDetail extends MyResponse {
  respondent_member_id?: string | null;
  answers?: SubmissionAnswer[];
}

interface Envelope<T> {
  success?: boolean;
  data: T;
}

export async function fetchAvailableSurveys(): Promise<SurveyView[]> {
  const { data } = await tenantApi.get<Envelope<SurveyView[]>>('/survey-responses/available');
  return data.data ?? [];
}

export async function fetchResidentSurvey(id: string): Promise<SurveyView> {
  const { data } = await tenantApi.get<Envelope<SurveyView>>(
    `/survey-responses/available/${id}`,
  );
  return data.data;
}

export async function fetchMyResponses(): Promise<MyResponse[]> {
  const { data } = await tenantApi.get<Envelope<MyResponse[]>>('/survey-responses/my');
  return data.data ?? [];
}

export async function fetchMyResponseDetail(surveyInstanceId: string): Promise<SubmittedResponseDetail> {
  const { data } = await tenantApi.get<Envelope<SubmittedResponseDetail>>(
    `/survey-responses/my/${surveyInstanceId}`,
  );
  return data.data;
}

export interface SubmitSurveyPayload {
  survey_template_id: string;
  submission_source?: 'portal' | 'public_link' | 'ccs' | 'mobile';
  instance_data?: Record<string, unknown>;
}

export async function submitSurveyResponse(payload: SubmitSurveyPayload): Promise<SurveyResponse> {
  const { data } = await tenantApi.post<Envelope<SurveyResponse>>('/survey-responses', {
    submission_source: 'mobile',
    ...payload,
  });
  return data.data;
}

export async function fetchPublicSurveyByToken(token: string): Promise<SurveyView> {
  const { data } = await tenantApi.get<Envelope<SurveyView>>(
    `/public/survey-responses/${token}`,
  );
  return data.data;
}

export async function submitPublicSurveyResponse(
  token: string,
  payload: Omit<SubmitSurveyPayload, 'submission_source'>,
): Promise<SurveyResponse> {
  const { data } = await tenantApi.post<Envelope<SurveyResponse>>(
    `/public/survey-responses/${token}`,
    { submission_source: 'public_link', ...payload },
  );
  return data.data;
}
